import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { Link, useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import { useUserContext } from "../../context/UserProvider";
import Spinner from "../util/Spinner";
import { EventAvailableIcon, MedalIcon, TruckIcon } from "../util/Icons";

function formatMoney(amount, currency = "cad") {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: String(currency || "cad").toUpperCase(),
  }).format(Number(amount || 0) / 100);
}

export default function StoreConfirmationPage() {
  const { user } = useUserContext();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadOrder() {
      if (!user?.uid || !orderId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const orderSnap = await getDoc(doc(db, "users", user.uid, "store_orders", orderId));
        if (!cancelled) {
          setOrder(orderSnap.exists() ? orderSnap.data() : null);
        }
      } catch (error) {
        console.error("Unable to load store order", error);
        if (!cancelled) {
          setLoadError("We couldn't load the order summary, but your checkout is complete.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadOrder();

    return () => {
      cancelled = true;
    };
  }, [orderId, user?.uid]);

  const cartItems = useMemo(() => {
    return Array.isArray(order?.cartItems) ? order.cartItems : [];
  }, [order]);
  const subtotal = order?.totals?.subtotal || cartItems.reduce((total, item) => total + Number(item.subtotal || 0), 0);
  const currency = order?.totals?.currency || cartItems[0]?.currency || "cad";

  return (
    <div className="mx-auto mt-3 w-full max-w-3xl pb-8">
      <div className="rounded-lg border border-emerald-200 bg-white shadow-sm">
        <div className="border-b border-emerald-100 bg-emerald-50 px-[16px] py-[20px] sm:px-[28px] sm:py-[28px]">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-600 text-white">
            <EventAvailableIcon size={28} />
          </div>
          <h1 className="mb-2 text-2xl font-bold text-slate-950">Your order is confirmed</h1>
          <p className="mb-0 text-sm leading-6 text-slate-700">
            Thank you. Your store items will ship in the fall with your medal package.
          </p>
        </div>

        <div className="space-y-4 px-[12px] py-[16px] sm:space-y-5 sm:px-[28px] sm:py-[28px]">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-[16px] sm:p-[20px]">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <TruckIcon size={20} color="#0f766e" />
                Free shipping
              </div>
              <p className="mb-0 text-sm leading-5 text-slate-600">
                These items are added to the same shipment as your medal package.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-[16px] sm:p-[20px]">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <MedalIcon size={20} color="#f59e0b" />
                Fall fulfillment
              </div>
              <p className="mb-0 text-sm leading-5 text-slate-600">
                No extra shipping step is needed from your account.
              </p>
            </div>
          </div>

          {orderId && (
            <div className="rounded-lg border border-slate-200 p-[16px] sm:p-[20px]">
              <p className="mb-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Order ID</p>
              <p className="mb-0 break-all text-sm font-semibold text-slate-900">{orderId}</p>
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[140px] items-center justify-center">
              <Spinner size={72} />
            </div>
          ) : loadError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {loadError}
            </div>
          ) : cartItems.length > 0 ? (
            <div className="rounded-lg border border-slate-200 p-[16px] sm:p-[20px]">
              <h2 className="mb-3 text-lg font-semibold text-slate-950">Order summary</h2>
              <div className="space-y-3">
                {cartItems.map((item) => (
                  <div key={item.priceId || item.productId} className="grid grid-cols-[1fr,auto] gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="mb-0 truncate font-medium text-slate-800">{item.name}</p>
                      <p className="mb-0 text-slate-500">
                        {item.quantity} x {formatMoney(item.unitAmount, item.currency)}
                      </p>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(item.subtotal || item.unitAmount * item.quantity, item.currency)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2 border-t border-slate-200 pt-3 text-sm">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Shipping</span>
                  <span className="font-semibold text-emerald-700">Free</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">Total</span>
                  <span className="text-lg font-bold text-slate-950">{formatMoney(subtotal, currency)}</span>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white no-underline transition-colors hover:bg-slate-800"
            >
              Back to home
            </Link>
            <Link
              to="/store"
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-50"
            >
              Back to store
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
